
import React from 'react';
import { Check } from 'lucide-react';
import { motion } from 'framer-motion';
import { Progress } from '../progress';
import { AnimatedCloseButton } from '@/components/buttons/close-button';

interface ImagePreviewProps {
  file: File;
  previewUrl: string;
  uploadStatus: 'idle' | 'uploading' | 'success' | 'error';
  uploadProgress: number;
  errorMessage: string | null;
  aspectRatio: number;
  previewSize: 'sm' | 'md' | 'lg';
  onRemove: () => void;
}

const ImagePreview: React.FC<ImagePreviewProps> = ({
  file,
  previewUrl,
  uploadStatus,
  uploadProgress,
  errorMessage,
  aspectRatio,
  previewSize,
  onRemove
}) => {
  // Size classes for preview container
  const previewSizeClasses = {
    sm: 'h-40 w-40',
    md: 'h-56 w-56',
    lg: 'h-72 w-72'
  };

  return (
    <motion.div 
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="flex flex-col items-center gap-4"
    >
      <div className={`relative ${previewSizeClasses[previewSize]} overflow-hidden rounded-lg bg-black/5`}>
        <img 
          src={previewUrl} 
          alt="Preview" 
          className="w-full h-full object-cover"
          style={{ aspectRatio: aspectRatio }}
        />
        
        {/* Remove button */}
        {uploadStatus !== 'uploading' && uploadStatus !== 'success' && (
          <div className="absolute top-2 right-2">
            <AnimatedCloseButton 
              onClick={() => {
                onRemove(); // Fixed event handler to match required signature
              }}
              size={28}
              variant="danger"
              className="bg-black/50 hover:bg-black/70 text-white"
              data-test-id="image-preview-remove-button"
            />
          </div>
        )}
        
        {/* Success overlay */}
        {uploadStatus === 'success' && (
          <div className="absolute inset-0 flex items-center justify-center bg-emerald-500/40 backdrop-blur-sm">
            <div className="bg-white rounded-full p-2">
              <Check size={36} className="text-emerald-500" />
            </div>
          </div>
        )}
      </div>
      
      {/* File info */}
      <div className="text-sm text-center">
        <p className="font-medium truncate max-w-[260px]">{file.name}</p>
        <p className="text-muted-foreground">
          {(file.size / 1024 / 1024).toFixed(2)} MB
        </p>
      </div>
      
      {/* Progress bar */}
      {uploadStatus === 'uploading' && (
        <div className="w-full max-w-xs mt-2">
          <Progress value={uploadProgress} className="h-2" />
          <p className="text-xs text-right mt-1 text-muted-foreground">
            {Math.round(uploadProgress)}%
          </p>
        </div>
      )}
      
      {/* Error message */}
      {errorMessage && (
        <p className="text-sm text-red-500 mt-2">{errorMessage}</p>
      )}
    </motion.div>
  );
};

export default ImagePreview;
